import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CheckCircle2, ChevronDown, ChevronRight, Edit2, FileText, Plus, Trash2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { useDebounced } from "@/hooks/useDebounced";
import { useCurrency } from "@/contexts/CurrencyContext";
import { supabase } from "@/integrations/supabase/client";
import { useAccountingMutations, useExpenses } from "@/hooks/useAccounting";
import {
  filterExpensesView, groupExpensesView,
  type ExpenseFilters, type ExpenseGroupBy,
} from "@/lib/accounting/accountingView";
import { formatAccountingAmount } from "@/lib/accounting/format";
import type { Expense } from "@/services/edgeFunctionWrappers/accountingManagementWrapper";
import { cn } from "@/lib/utils";
import { ExpenseFormDialog } from "./ExpenseFormDialog";

/** Couleurs de statut : payé = vert, à payer = ambre, annulé = neutre. */
const STATUS_CLASS: Record<string, string> = {
  paid: "border-transparent bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300",
  due: "border-transparent bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-300",
  cancelled: "border-transparent bg-muted text-muted-foreground line-through",
};

const StatusBadge = ({ status, label }: { status: string; label: string }) => (
  <Badge className={cn("shrink-0 font-medium", STATUS_CLASS[status] ?? STATUS_CLASS.cancelled)}>
    {label}
  </Badge>
);

interface Props {
  period: { start_date: string; end_date: string };
}

/**
 * Journal des dépenses de la période : filtres par colonne (type, catégorie,
 * statut, recherche), regroupement repliable avec sous-totaux, statuts
 * colorés, table desktop / cartes mobiles.
 */
export function ExpensesJournalTab({ period }: Props) {
  const { t } = useTranslation(["accounting", "common"]);
  const { toast } = useToast();
  const { currency } = useCurrency();
  const fmt = (v: number) => formatAccountingAmount(v, currency);

  const [kind, setKind] = useState<ExpenseFilters["kind"]>("all");
  const [status, setStatus] = useState<ExpenseFilters["status"]>("all");
  const [category, setCategory] = useState<string>("all");
  const [groupBy, setGroupBy] = useState<ExpenseGroupBy>("kind");
  const [search, setSearch] = useState("");
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const debouncedSearch = useDebounced(search, 250);

  const { data: rows = [], isLoading } = useExpenses({
    start_date: period.start_date,
    end_date: period.end_date,
  });
  const { markPaid, deleteExpense } = useAccountingMutations();

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Expense | null>(null);
  const [deleting, setDeleting] = useState<Expense | null>(null);

  const categories = useMemo<string[]>(() => {
    const set = new Set<string>();
    for (const r of rows) if (r.category) set.add(String(r.category));
    return [...set].sort();
  }, [rows]);

  const filtered = useMemo(
    () => filterExpensesView(rows, { kind, status, category, search: debouncedSearch }),
    [rows, kind, status, category, debouncedSearch]
  );
  const groups = useMemo(() => groupExpensesView(filtered, groupBy), [filtered, groupBy]);
  const total = useMemo(() => filtered.reduce((s, r) => s + (Number(r.amount) || 0), 0), [filtered]);

  const groupLabel = (key: string) => {
    if (groupBy === "kind") return t(`kinds.${key}`, key);
    if (groupBy === "category") return t(`categories.${key}`, key);
    if (groupBy === "status") return t(`statuses.${key}`, key);
    return t("journal.total");
  };

  const openAttachment = async (e: Expense) => {
    if (!e.attachment_path) return;
    const { data, error } = await supabase.storage
      .from("supplier-invoices")
      .createSignedUrl(e.attachment_path, 60 * 10);
    if (error || !data?.signedUrl) {
      toast({ title: t("common:error"), description: t("journal.attachmentError"), variant: "destructive" });
      return;
    }
    window.open(data.signedUrl, "_blank", "noopener");
  };

  const onMarkPaid = async (e: Expense) => {
    try {
      await markPaid.mutateAsync({ id: e.id });
      toast({ title: t("journal.markedPaid") });
    } catch (err: any) {
      toast({ title: t("common:error"), description: err?.message ?? "", variant: "destructive" });
    }
  };

  const onDelete = async () => {
    if (!deleting) return;
    try {
      await deleteExpense.mutateAsync(deleting.id);
      toast({ title: t("journal.deleted") });
      setDeleting(null);
    } catch (err: any) {
      toast({ title: t("common:error"), description: err?.message ?? "", variant: "destructive" });
    }
  };

  const actionButtons = (e: Expense) => (
    <div className="flex items-center gap-1 justify-end">
      {e.status === "due" && (
        <Button variant="ghost" size="icon" className="h-9 w-9" onClick={() => onMarkPaid(e)} aria-label={t("journal.markPaid")}>
          <CheckCircle2 className="h-4 w-4 text-emerald-600" />
        </Button>
      )}
      {e.attachment_path && (
        <Button variant="ghost" size="icon" className="h-9 w-9" onClick={() => openAttachment(e)} aria-label={t("journal.openAttachment")}>
          <FileText className="h-4 w-4" />
        </Button>
      )}
      <Button variant="ghost" size="icon" className="h-9 w-9" onClick={() => { setEditing(e); setFormOpen(true); }} aria-label={t("common:edit", { defaultValue: "Modifier" })}>
        <Edit2 className="h-4 w-4" />
      </Button>
      <Button variant="ghost" size="icon" className="h-9 w-9" onClick={() => setDeleting(e)} aria-label={t("journal.delete")}>
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  );

  const toggleGroup = (key: string) => setCollapsed((c) => ({ ...c, [key]: !c[key] }));

  const groupHeader = (key: string, count: number, amount: number, isCollapsed: boolean) => (
    <button
      type="button"
      onClick={() => toggleGroup(key)}
      className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-muted/60"
      aria-expanded={!isCollapsed}
    >
      {isCollapsed ? <ChevronRight className="h-4 w-4 shrink-0" /> : <ChevronDown className="h-4 w-4 shrink-0" />}
      <span className="font-semibold text-sm">{groupLabel(key)}</span>
      <span className="text-xs text-muted-foreground">{t("journal.entries", { count })}</span>
      <span className="ml-auto text-sm font-semibold tabular-nums">{fmt(amount)}</span>
    </button>
  );

  return (
    <div className="space-y-3">
      {/* Filtres par colonne + regroupement + total */}
      <div className="flex flex-col gap-2 md:flex-row md:flex-wrap md:items-center">
        <Input
          type="search"
          placeholder={t("journal.search")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="h-10 text-base md:text-sm md:max-w-xs"
        />
        <div className="grid grid-cols-2 gap-2 md:flex">
          <Select value={kind ?? "all"} onValueChange={(v) => setKind(v as ExpenseFilters["kind"])}>
            <SelectTrigger className="h-10 md:h-9 md:w-[170px]" aria-label={t("journal.colKind")}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("journal.allKinds")}</SelectItem>
              <SelectItem value="expense">{t("kinds.expense")}</SelectItem>
              <SelectItem value="supplier_invoice">{t("kinds.supplier_invoice")}</SelectItem>
              <SelectItem value="salary">{t("kinds.salary")}</SelectItem>
            </SelectContent>
          </Select>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="h-10 md:h-9 md:w-[170px]" aria-label={t("journal.colCategory")}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("journal.allCategories")}</SelectItem>
              {categories.map((c) => (
                <SelectItem key={c} value={c}>{t(`categories.${c}`, c)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={status ?? "all"} onValueChange={(v) => setStatus(v as ExpenseFilters["status"])}>
            <SelectTrigger className="h-10 md:h-9 md:w-[150px]" aria-label={t("journal.colStatus")}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("journal.allStatuses")}</SelectItem>
              <SelectItem value="paid">{t("statuses.paid")}</SelectItem>
              <SelectItem value="due">{t("statuses.due")}</SelectItem>
              <SelectItem value="cancelled">{t("statuses.cancelled")}</SelectItem>
            </SelectContent>
          </Select>
          <Select value={groupBy} onValueChange={(v) => setGroupBy(v as ExpenseGroupBy)}>
            <SelectTrigger className="h-10 md:h-9 md:w-[190px]" aria-label={t("journal.groupBy")}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="kind">{t("journal.groupBy")} : {t("journal.groupKind")}</SelectItem>
              <SelectItem value="category">{t("journal.groupBy")} : {t("journal.groupCategory")}</SelectItem>
              <SelectItem value="status">{t("journal.groupBy")} : {t("journal.groupStatus")}</SelectItem>
              <SelectItem value="none">{t("journal.groupNone")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="md:ml-auto flex items-center justify-between gap-3">
          <span className="text-sm text-muted-foreground">
            {t("journal.total")} : <b className="tabular-nums text-foreground">{fmt(total)}</b>
          </span>
          <Button size="sm" className="h-9 hidden md:inline-flex" onClick={() => { setEditing(null); setFormOpen(true); }}>
            <Plus className="h-4 w-4 mr-1.5" /> {t("journal.add")}
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-20 rounded-xl" />)}
        </div>
      ) : filtered.length === 0 ? (
        <Card><CardContent className="py-10 text-center text-sm text-muted-foreground">{t("journal.empty")}</CardContent></Card>
      ) : (
        <div className="space-y-3">
          {groups.map((group) => {
            const isCollapsed = !!collapsed[group.key];
            return (
              <Card key={group.key} className="overflow-hidden">
                {groupBy !== "none" && (
                  <div className="border-b bg-muted/30">
                    {groupHeader(group.key, group.rows.length, group.total, isCollapsed)}
                  </div>
                )}
                {!isCollapsed && (
                  <>
                    {/* Cartes mobiles */}
                    <div className="md:hidden divide-y">
                      {group.rows.map((e) => (
                        <div key={e.id} className="p-3 space-y-2">
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <div className="font-medium truncate">{e.label}</div>
                              <div className="text-xs text-muted-foreground truncate">
                                {e.expense_date} • {t(`kinds.${e.kind}`)} • {t(`categories.${e.category}`, e.category)}
                                {e.suppliers?.name ? ` • ${e.suppliers.name}` : ""}
                                {e.employee_name ? ` • ${e.employee_name}` : ""}
                              </div>
                            </div>
                            <StatusBadge status={e.status} label={t(`statuses.${e.status}`)} />
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-base font-semibold tabular-nums">{fmt(Number(e.amount))}</span>
                            {actionButtons(e)}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Table desktop */}
                    <CardContent className="hidden md:block p-0 overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>{t("journal.colLabel")}</TableHead>
                            <TableHead>{t("journal.colKind")}</TableHead>
                            <TableHead>{t("journal.colCategory")}</TableHead>
                            <TableHead>{t("journal.colDate")}</TableHead>
                            <TableHead>{t("journal.colStatus")}</TableHead>
                            <TableHead className="text-right">{t("journal.colAmount")}</TableHead>
                            <TableHead className="text-right">{t("journal.colActions")}</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {group.rows.map((e) => (
                            <TableRow key={e.id}>
                              <TableCell className="font-medium">
                                <div className="truncate max-w-[260px]">{e.label}</div>
                                <div className="text-xs text-muted-foreground truncate max-w-[260px]">
                                  {e.suppliers?.name ?? e.employee_name ?? e.invoice_number ?? ""}
                                </div>
                              </TableCell>
                              <TableCell>{t(`kinds.${e.kind}`)}</TableCell>
                              <TableCell>{t(`categories.${e.category}`, e.category)}</TableCell>
                              <TableCell className="whitespace-nowrap">{e.expense_date}</TableCell>
                              <TableCell>
                                <StatusBadge status={e.status} label={t(`statuses.${e.status}`)} />
                              </TableCell>
                              <TableCell className="text-right tabular-nums font-medium whitespace-nowrap">
                                {fmt(Number(e.amount))}
                              </TableCell>
                              <TableCell>{actionButtons(e)}</TableCell>
                            </TableRow>
                          ))}
                          {groupBy !== "none" && (
                            <TableRow className="bg-muted/30 hover:bg-muted/30">
                              <TableCell colSpan={5} className="text-sm font-medium">
                                {t("journal.subtotal")} — {groupLabel(group.key)}
                              </TableCell>
                              <TableCell className="text-right tabular-nums font-semibold whitespace-nowrap">
                                {fmt(group.total)}
                              </TableCell>
                              <TableCell />
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {/* FAB mobile */}
      <Button
        onClick={() => { setEditing(null); setFormOpen(true); }}
        className="md:hidden fixed right-4 bottom-[calc(env(safe-area-inset-bottom)+1rem)] z-40 h-14 w-14 rounded-full shadow-lg"
        aria-label={t("journal.add")}
      >
        <Plus className="h-6 w-6" />
      </Button>

      <ExpenseFormDialog open={formOpen} onOpenChange={setFormOpen} expense={editing} periodMonth={period.start_date.slice(0, 7)} />

      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("journal.confirmDelete")}</AlertDialogTitle>
            <AlertDialogDescription>{deleting?.label}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common:cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={onDelete}>{t("journal.delete")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
